import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { botsApi, type BotInlineKeyboard } from '@/api/bots';

interface Props {
  keyboard: BotInlineKeyboard;
  messageId?: string;
  dmId?: string;
}

/**
 * Inline-клавиатура бот-сообщения. URL-кнопки открываются в новой вкладке,
 * callback-кнопки бьют по `/api/bots/callback`, который проверяет подпись
 * через `callbackDataMatchesKeyboard` на сервере.
 */
export default function InlineKeyboard({ keyboard, messageId, dmId }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onClick = async (data: string) => {
    setError(null);
    setBusy(data);
    try {
      await botsApi.sendCallback({ messageId, dmId, data });
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Не удалось обработать клик';
      setError(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-1">
      {keyboard.map((row, ri) => (
        <div key={ri} className="flex gap-1">
          {row.map((btn, bi) => {
            const isUrl = !!btn.url;
            const key = `${ri}:${bi}`;
            const isBusy = busy === btn.callbackData;
            return (
              <button
                key={key}
                disabled={isBusy}
                onClick={() => {
                  if (isUrl && btn.url) {
                    window.open(btn.url, '_blank', 'noopener,noreferrer');
                  } else if (btn.callbackData) {
                    onClick(btn.callbackData);
                  }
                }}
                className={clsx(
                  'flex-1 min-w-0 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                  'border border-surface-700 bg-surface-800 hover:bg-surface-700 text-surface-100',
                  isBusy && 'opacity-60',
                )}
              >
                <span className="inline-flex items-center gap-1.5 justify-center">
                  {isBusy && <Loader2 size={12} className="animate-spin" />}
                  {isUrl && !isBusy && <ExternalLink size={12} />}
                  <span className="truncate">{btn.text}</span>
                </span>
              </button>
            );
          })}
        </div>
      ))}
      {error && <div className="text-[11px] text-red-400 mt-1">{error}</div>}
    </div>
  );
}
